import { NewCollectionObservationForm } from "@/components/observations/NewCollectionObservationForm"
import { getObservationForCollection } from "@/hooks/useObservationForCollection"
import { useObservation } from "@/hooks/useObservation"
import { powerSyncDb } from "@/lib/powersync/db"
import { Spinner } from "@fieldbook/ui/spinner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@fieldbook/ui/alert-dialog"
import { createFileRoute, ErrorComponentProps } from "@tanstack/react-router"
import * as z from "zod"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"

const searchSchema = z.object({
  siteId: z.string().optional(),
  tagCode: z.string().min(8).max(8).optional(),
  duplicateFromId: z.string().optional(),
  draftId: z.string().uuid().optional(),
})

class TagCodeUsedError extends Error {
  readonly name = "TagCodeUsedError" as const

  constructor(
    message: string,
    public observationId: string,
  ) {
    super(message)
    // Ensure the prototype chain is correct
    Object.setPrototypeOf(this, TagCodeUsedError.prototype)
  }
}

export const Route = createFileRoute(
  "/_private/projects/$id/observations/new/collection",
)({
  component: NewCollectionObservation,
  validateSearch: (search) => searchSchema.parse(search),
  loaderDeps: ({ search: { tagCode } }) => ({ tagCode }),
  loader: async ({ deps: { tagCode }, params: { id: projectId } }) => {
    if (tagCode) {
      // Check if tag code already exists in local PowerSync DB
      const existingCollection = await powerSyncDb.getOptional<{ id: string }>(
        "SELECT id FROM collections WHERE project_id = ? AND nano_id = ?",
        [projectId, tagCode],
      )

      if (existingCollection) {
        const observation = await getObservationForCollection(
          existingCollection.id,
        )
        if (!observation)
          throw new Error(
            `Tag code ${tagCode} has already been used for a collection`,
          )

        throw new TagCodeUsedError(
          `Tag code ${tagCode} has already been used for a collection`,
          observation.id,
        )
      }
    }
    return { tagCode }
  },
  errorComponent: ErrorComponent,
})

function isTagCodeUsedError(error: unknown): error is TagCodeUsedError {
  return error instanceof Error && error.name === "TagCodeUsedError"
}

function ErrorComponent({ error }: ErrorComponentProps) {
  const nav = Route.useNavigate()
  const { id } = Route.useParams()
  const isTagCodeError = isTagCodeUsedError(error)

  return (
    <AlertDialog open={true}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Error</AlertDialogTitle>
          <AlertDialogDescription className="flex flex-col gap-2 text-lg">
            {error.message}
          </AlertDialogDescription>
          {isTagCodeError && (
            <AlertDialogAction
              onClick={() =>
                nav({
                  to: "/projects/$id/data/observations/$observationId",
                  params: { id, observationId: error.observationId },
                })
              }
            >
              View Collection Details
            </AlertDialogAction>
          )}
          <AlertDialogCancel onClick={() => nav({ to: "/projects/$id" })}>
            Close
          </AlertDialogCancel>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function NewCollectionObservation() {
  // This route covers the project map for its whole life, loading states
  // included. Releasing the map's WebGL context and tile buffers here keeps
  // the process small enough to survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId } = Route.useParams()
  const { siteId, duplicateFromId, draftId } = Route.useSearch()
  const loaderData = Route.useLoaderData()

  const { data: duplicateFrom, isPending: isDuplicatePending } = useObservation(
    { observationId: duplicateFromId ?? "", projectId },
  )

  if (duplicateFromId && isDuplicatePending) {
    return <Spinner />
  }

  return (
    <NewCollectionObservationForm
      projectId={projectId}
      siteId={siteId}
      tagCode={loaderData?.tagCode}
      duplicateFrom={duplicateFromId ? (duplicateFrom ?? undefined) : undefined}
      draftId={draftId}
    />
  )
}
